import {
  useWSModalCustom,
  WSDataItemProps,
  WSIconProps,
  WSInfoBox,
  WSModal,
  WSPillProps
} from "@wingspanhq/fe-component-library";
import {
  ComplianceEntity,
  ComplianceEntityTaxClassification,
  ComplianceEntityType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { DataView } from "../../../shared/components/DataView";
import { selectorCountryName } from "../../../shared/selectors/selectorCountryName";
import {
  ComplianceEntityTaxVerification,
  selectorComplianceEntityTaxVerification
} from "../../../shared/selectors/selectorComplianceEntityTaxVerification";
import {
  getTaxIdLabel,
  getTaxIdVerificationAlert
} from "../../../shared/utils/taxId";
import {
  PATH_ONBOARDING_CHANGE,
  PATH_ONBOARDING_VERIFICAIONS_TAX
} from "../../Onboarding";
import { alertMissingDataProps } from "../constants/alerts";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

type Props = {
  complianceEntity?: ComplianceEntity;
  missingFields?: string[];
};

function getVerificationStatusPill(
  status: ComplianceEntityTaxVerification
): WSPillProps | undefined {
  if (status === "Verified") {
    return { theme: "success", icon: true, text: "Verified" };
  }

  if (status === "Pending") {
    return { theme: "warning", icon: true, text: "Verification pending" };
  }

  if (status === "Failed") {
    return { theme: "error", icon: true, text: "Failed" };
  }

  return { theme: "neutral", text: "Not verified" };
}

const TAX_CLASSIFICATION_LABEL: Record<
  ComplianceEntityTaxClassification,
  string
> = {
  [ComplianceEntityTaxClassification.TAXED_AS_S_CORPORATION]: "S Corporation",
  [ComplianceEntityTaxClassification.TAXED_AS_C_CORPORATION]: "C Corporation",
  [ComplianceEntityTaxClassification.TAXED_AS_PARTNERSHIP]: "Partnership",
  [ComplianceEntityTaxClassification.TAXED_AS_DISREGARDED_ENTITY]:
    "Disregarded entity / LLC"
};

const verifiedIcon: WSIconProps = {
  name: "check-circle",
  color: "green400"
};

export const DataViewTaxpayerInformation: React.FC<Props> = ({
  complianceEntity,
  missingFields
}) => {
  const featureFlags = useFeatureFlags();

  const history = useHistory();
  const modalInfo = useWSModalCustom(ModalInfo);

  const country =
    complianceEntity?.jurisdictionCountry ||
    complianceEntity?.physicalAddress?.country ||
    "US";

  const isBusiness = complianceEntity?.type === ComplianceEntityType.BUSINESS;
  const accountType = isBusiness ? "Business" : "Individual";

  const verificationStatus = useMemo(
    () => selectorComplianceEntityTaxVerification(complianceEntity),
    [complianceEntity]
  );
  const isVerified = verificationStatus === "Verified";

  const taxIdProvided = (complianceEntity?.taxIdentifiers ?? []).some(
    taxId => taxId.hasValue
  );

  const nameItems = useMemo((): (WSDataItemProps | null)[] => {
    if (isBusiness) {
      return [
        {
          label: "Legal business name",
          value: complianceEntity?.legalName || undefined,
          icon: isVerified ? verifiedIcon : undefined,
          alert: missingFields?.includes("legalBusinessName")
            ? alertMissingDataProps
            : undefined
        }
      ];
    }

    const name = complianceEntity?.individualLegalName;

    return [
      {
        label: "Legal first name",
        value: name?.givenName || undefined,
        icon: isVerified ? verifiedIcon : undefined,
        alert: missingFields?.includes("firstName")
          ? alertMissingDataProps
          : undefined
      },
      name?.middleNames
        ? {
            label: "Middle name",
            value: name.middleNames,
            icon: isVerified ? verifiedIcon : undefined
          }
        : null,
      {
        label: "Legal last name",
        value: name?.familyName || undefined,
        icon: isVerified ? verifiedIcon : undefined,
        alert: missingFields?.includes("lastName")
          ? alertMissingDataProps
          : undefined
      }
    ];
  }, [
    complianceEntity?.individualLegalName,
    complianceEntity?.legalName,
    isBusiness,
    isVerified,
    missingFields
  ]);

  return (
    <DataView
      header={{
        label: "Taxpayer information",
        value: {
          action: {
            label: "Request update",
            onClick: () => {
              if (verificationStatus === "None") {
                history.push(PATH_ONBOARDING_VERIFICAIONS_TAX);
              } else if (verificationStatus === "Verified") {
                history.push(PATH_ONBOARDING_CHANGE);
              } else {
                modalInfo.open({
                  onContinue: () => {
                    history.push(PATH_ONBOARDING_CHANGE);
                  }
                });
              }
            }
          },
          pill: getVerificationStatusPill(verificationStatus)
        }
      }}
      data={[
        {
          label: "Country of tax residency",
          value: selectorCountryName(country),
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: "Business type",
          value: accountType,
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: "Federal tax classification",
          value: isBusiness
            ? complianceEntity?.taxClassification
              ? TAX_CLASSIFICATION_LABEL[complianceEntity.taxClassification]
              : undefined
            : "Sole proprietorship / Single member LLC",
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: getTaxIdLabel(accountType, country),
          value: taxIdProvided ? "Provided" : undefined,
          icon: isVerified ? verifiedIcon : undefined,
          alert: getTaxIdVerificationAlert({
            country,
            status: verificationStatus,
            missingFields,
            isPendingPaused: featureFlags.data?.is1099FilingPaused
          })
        },
        ...nameItems
      ]}
    />
  );
};

const ModalInfo: React.FC<{
  onClose: () => void;
  onContinue: () => void;
}> = props => (
  <WSModal
    onClose={props.onClose}
    divider
    size="S"
    title="Request update to taxpayer information"
    actions={{
      alignment: "fill",
      buttons: [
        {
          kind: "Secondary",
          label: "Cancel",
          onClick: props.onClose
        },
        {
          label: "Continue",
          onClick: props.onContinue
        }
      ]
    }}
  >
    <WSInfoBox title="What to expect:">
      <ol>
        <li>
          You will need to complete and submit your updated information to be
          re-verified.
        </li>
        <li>Your previous information will be discarded.</li>
        <li>
          Your client(s) will be informed of any tax-related changes to your
          profile for compliance and tax reporting purposes.
        </li>
      </ol>
    </WSInfoBox>
  </WSModal>
);
